import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { GlobalService } from '../../services/global.service';
import { LoginService } from '../../services/login.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-aadhaar-verify-page',
  imports: [CommonModule],
  templateUrl: './aadhaar-verify-page.component.html',
  styleUrl: './aadhaar-verify-page.component.css'
})
export class AadhaarVerifyPageComponent implements OnInit, OnDestroy {
  step: 1 | 2 = 1;

  aadhaar_qr_image = '';
  aadhaar_request_id = '';
  aadhaar_status: 'pending' | 'success' | 'failed' = 'pending';
  private aadhaar_polling: any = null;

  face_temp_token = '';
  face_login_response: any = null;
  is_first_login = false;
  face_loading = false;
  face_error = '';
  face_image_base64 = '';
  camera_stream: MediaStream | null = null;

  @ViewChild('videoEl') videoEl!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl') canvasEl!: ElementRef<HTMLCanvasElement>;

  private nav_state: any;

  constructor(
    private router: Router,
    public gs: GlobalService,
    private login: LoginService,
    private us: UserService
  ) {
    this.nav_state = this.router.getCurrentNavigation()?.extras.state ?? {};
  }

  get user_name(): string {
    const u = this.gs.user_data;
    if (!u) return '';
    return u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || '';
  }

  ngOnInit(): void {
    const s = this.nav_state;
    this.aadhaar_qr_image = s['qr_image'] ?? '';
    this.aadhaar_request_id = s['request_id'] ?? '';
    this.face_temp_token = s['token'] ?? '';
    this.face_login_response = s['login_response'] ?? null;
    this.is_first_login = s['first_login'] ?? false;

    if (s['step'] === 2) {
      this.step = 2;
      setTimeout(() => this.startCamera(), 150);
    } else if (this.aadhaar_request_id) {
      this.startAadhaarPolling();
    }
  }

  // ─── Aadhaar ───────────────────────────────────────────────────────────────

  startAadhaarPolling() {
    this.stopAadhaarPolling();
    this.aadhaar_polling = setInterval(async () => {
      try {
        const res = await this.login.aadhaar_check_status({ request_id: this.aadhaar_request_id });
        if (res.message === 'pending') return;

        this.stopAadhaarPolling();

        if (res.message === 'face_verification_required') {
          this.aadhaar_status = 'success';
          this.face_temp_token = res.data.token;
          this.face_login_response = res;
          this.is_first_login = res.data.first_login;
          this.step = 2;
          setTimeout(() => this.startCamera(), 150);
        } else if (res.data) {
          this.aadhaar_status = 'success';
          await this.completeLogin(res);
        } else {
          this.aadhaar_status = 'failed';
          this.gs.toastr_shows_function(res.message || 'Aadhaar verification failed', 'Error', 'error');
        }
      } catch {
        this.stopAadhaarPolling();
        this.aadhaar_status = 'failed';
        this.gs.toastr_shows_function('Aadhaar verification failed', 'Error', 'error');
      }
    }, 5000);
  }

  stopAadhaarPolling() {
    if (this.aadhaar_polling) {
      clearInterval(this.aadhaar_polling);
      this.aadhaar_polling = null;
    }
  }

  retryAadhaar() {
    this.stopAadhaarPolling();
    this.aadhaar_status = 'pending';
    if (this.aadhaar_request_id) this.startAadhaarPolling();
  }

  // ─── Face ──────────────────────────────────────────────────────────────────

  async startCamera() {
    this.face_error = '';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      this.camera_stream = stream;
      const video = this.videoEl?.nativeElement;
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
    } catch {
      this.face_error = 'Camera access denied. Please allow camera permissions and try again.';
    }
  }

  stopCamera() {
    this.camera_stream?.getTracks().forEach(t => t.stop());
    this.camera_stream = null;
  }

  captureImage() {
    const video = this.videoEl?.nativeElement;
    const canvas = this.canvasEl?.nativeElement;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0);
    }
    this.face_image_base64 = canvas.toDataURL('image/jpeg', 0.85);
    this.stopCamera();
  }

  retakeFace() {
    this.face_image_base64 = '';
    this.face_error = '';
    setTimeout(() => this.startCamera(), 150);
  }

  async submitFaceVerification() {
    if (!this.face_image_base64 || this.face_loading) return;
    this.face_loading = true;
    this.face_error = '';
    try {
      const image_path = await this.login.upload_image(this.face_image_base64, this.face_temp_token);
      const res = this.is_first_login
        ? await this.login.face_register(image_path, this.face_temp_token)
        : await this.login.face_verify(image_path, this.face_temp_token);

      if (res.code === 200) {
        const complete_response = {
          ...this.face_login_response,
          data: { ...this.face_login_response.data, first_login: false }
        };
        await this.completeLogin(complete_response);
      } else {
        this.face_error = res.message || 'Face verification failed. Please try again.';
      }
    } catch (error: any) {
      this.face_error = error?.error?.message || 'Upload or verification failed. Please try again.';
    } finally {
      this.face_loading = false;
    }
  }

  // ─── Shared ────────────────────────────────────────────────────────────────

  private async completeLogin(response: any) {
    this.gs.user_data = response.data;
    localStorage.setItem('app_user', JSON.stringify(this.gs.user_data));
    const role_data = await this.us.get(response.data.id);
    this.gs.user_role_data = role_data.data.Role ?? role_data.data?.user?.Role;
    localStorage.setItem('user_role', JSON.stringify(this.gs.user_role_data));
    this.gs.check_and_fetch_from_local_storage();
    this.gs.toastr_shows_function(response?.message || 'Verified successfully', 'Success', 'success');
    const landing_page = this.gs.user_role_data?.landing_page || '/dashboard';
    this.router.navigate([landing_page]);
  }

  ngOnDestroy(): void {
    this.stopAadhaarPolling();
    this.stopCamera();
  }
}
